import classNames from "classnames"
import type SwiperInstance from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

import { ONLINE_SERVICE_LINKS } from "../SolutionFinder/constants"
import type { SolutionFilters, SolutionItem } from "../SolutionFinder/types"
import {
  useResolvedSolution,
  type ResolvedOnlineProductItem,
  type ResolvedPurchasableItem,
  type ResolvedSolutionItem,
} from "../useResolvedSolution"

import Link from "@components/elements/Link"
import CircledIconButton from "@components/elements/V2/Buttons/CircledIconButton"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import Typography from "@components/elements/V2/Typography"
import ProductCard from "@components/V2/Cards/Product"
import { Color } from "@constants/V2/color"
import { parseConsolidatedPrice } from "@hooks/V2/storyblok/useConsolidatedItem"
import useIsomorphicLayoutEffect from "@hooks/V2/useIsomorphicLayoutEffect"
import { getSavingsBadge } from "@utils/shop"
import React, { useMemo, useRef, useState } from "react"

interface Props {
  solution: SolutionItem[]
  filters?: SolutionFilters
  showCartControls?: boolean
  showLinks?: boolean
  sortByPriority?: boolean
  showNavigationArrows?: boolean
}

const SolutionDisplay = ({
  solution,
  filters,
  showCartControls = false,
  showLinks = true,
  sortByPriority = true,
  showNavigationArrows = false,
}: Props) => {
  const resolvedSolution = useResolvedSolution(solution)

  const swiperReference = useRef<SwiperInstance | null>(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)

  const updateEdgeState = (swiper: SwiperInstance) => {
    setIsAtStart(swiper.isBeginning)
    setIsAtEnd(swiper.isEnd)
  }

  const displayedSolution = useMemo(
    () =>
      sortByPriority
        ? [...resolvedSolution].sort(
            (a, b) => (a.item.priority || 99) - (b.item.priority || 99)
          )
        : resolvedSolution,
    [resolvedSolution, sortByPriority]
  )

  const selectedFilterCategory = filters?.categories.find(
    (category) => category.id === filters.selectedId
  )

  const isFiltering = selectedFilterCategory !== undefined

  const visibleItemValues = useMemo(
    () =>
      new Set(
        selectedFilterCategory
          ? selectedFilterCategory.items.map((item) => item.value)
          : displayedSolution.map((resolved) => resolved.item.value)
      ),
    [selectedFilterCategory, displayedSolution]
  )

  const orderedSolution = useMemo(() => {
    if (!isFiltering) return displayedSolution

    return [
      ...displayedSolution.filter((resolved) =>
        visibleItemValues.has(resolved.item.value)
      ),
      ...displayedSolution.filter(
        (resolved) => !visibleItemValues.has(resolved.item.value)
      ),
    ]
  }, [displayedSolution, isFiltering, visibleItemValues])

  const visibleValuesKey = [...visibleItemValues].join(",")

  useIsomorphicLayoutEffect(() => {
    if (!isFiltering) return

    const swiper = swiperReference.current

    if (!swiper) return

    swiper.update()
    swiper.slideTo(0, 0, false)
    updateEdgeState(swiper)
  }, [isFiltering, visibleValuesKey])

  const getProductCard = ({
    item,
    config: product,
    consolidatedItem,
  }: ResolvedPurchasableItem) => {
    const productPrice = parseConsolidatedPrice(consolidatedItem.price)
    const originalPrice = parseConsolidatedPrice(consolidatedItem.originalPrice)

    const effectiveBadge =
      (productPrice
        ? getSavingsBadge(productPrice, originalPrice)
        : consolidatedItem.badge) ?? undefined

    return (
      <ProductCard
        key={product.name}
        backgroundColor={Color.White}
        imageAlt={consolidatedItem.thumbnail?.alt || ""}
        imageUrl={consolidatedItem.thumbnail?.filename || ""}
        productName={item.name || consolidatedItem.name}
        shortDescription={
          item.description || consolidatedItem.shortDescription || ""
        }
        priceAdornmentText="R"
        priceValue={productPrice}
        wasPrice={consolidatedItem.onPromotion ? originalPrice : undefined}
        superScript={item.superscript}
        learnMoreButtonUrl={
          showLinks
            ? (consolidatedItem.detailsLink ?? consolidatedItem.ctaLink)
            : undefined
        }
        showNumberControls={showCartControls}
        purchasableItem={consolidatedItem}
        reviewSkus={consolidatedItem.reviewSkus}
        badge={effectiveBadge}
      />
    )
  }

  const getCombinedOnlineCard = (item: SolutionItem) => {
    const renderServiceLabel = (link: string, text: string) => {
      const label = (
        <Typography
          element="span"
          text={text}
          font="grotesk"
          weight="book"
          size="body-sm"
          color={showLinks ? Color.Blue : Color.Charcoal}
        />
      )

      return showLinks ? <Link link={link}>{label}</Link> : label
    }

    const descriptionContent = (
      <>
        <p>
          <Typography
            element="span"
            text="Get a "
            font="grotesk"
            weight="book"
            size="body-sm"
            color={Color.Charcoal}
          />
          {renderServiceLabel(ONLINE_SERVICE_LINKS.gateway, "Payment Gateway")}
          <Typography
            element="span"
            text=" for your website or simply send "
            font="grotesk"
            weight="book"
            size="body-sm"
            color={Color.Charcoal}
          />
          {renderServiceLabel(
            ONLINE_SERVICE_LINKS.payment_request,
            "Payment Links"
          )}
          <Typography
            element="span"
            text=" or "
            font="grotesk"
            weight="book"
            size="body-sm"
            color={Color.Charcoal}
          />
          {renderServiceLabel(ONLINE_SERVICE_LINKS.invoices, "Invoices")}
        </p>
        <Typography
          text="Activate FREE after sign up"
          highlightWeight="medium"
          highlightedText="FREE"
          highlightColor={Color.Charcoal}
          font="grotesk"
          weight="book"
          size="body-sm"
          color={Color.Charcoal}
          className="mt-20"
        />
      </>
    )

    return (
      <ProductCard
        key={item.value}
        backgroundColor={Color.White}
        imageAlt={item.name || ""}
        imageUrl={item.image || ""}
        productName={item.name || ""}
        superScript={item.superscript}
        shortDescription={descriptionContent}
        hidePrice
      />
    )
  }

  const getOnlineCard = ({
    item,
    consolidatedItem,
  }: ResolvedOnlineProductItem) => {
    return (
      <ProductCard
        key={item.value}
        backgroundColor={Color.White}
        imageAlt={consolidatedItem.thumbnail?.alt || ""}
        imageUrl={consolidatedItem.thumbnail?.filename || ""}
        productName={consolidatedItem.name}
        superScript={item.superscript}
        shortDescription={consolidatedItem.shortDescription}
        priceLabelOverride={consolidatedItem.priceLabelOverride}
        hidePrice
        learnMoreButtonUrl={showLinks ? consolidatedItem.ctaLink : undefined}
      />
    )
  }

  const getResultCard = (resolved: ResolvedSolutionItem) => {
    switch (resolved.kind) {
      case "combinedOnline":
        return getCombinedOnlineCard(resolved.item)
      case "onlineProduct":
        return getOnlineCard(resolved)
      case "purchasable":
        return getProductCard(resolved)
    }
  }

  const resultCards = orderedSolution.map((resolved) => ({
    item: resolved.item,
    card: getResultCard(resolved),
  }))

  const lastVisibleIndex = resultCards.reduce(
    (lastIndex, { item }, index) =>
      visibleItemValues.has(item.value) ? index : lastIndex,
    -1
  )

  return (
    <>
      {(filters || showNavigationArrows) && (
        <div className="md:grid md:grid-cols-[1fr_auto_1fr] md:items-center">
          {filters && (
            <div className="flex gap-10 overflow-x-auto pb-10 sm:justify-center sm:pb-0 md:col-start-2">
              {filters.categories.map((category) => {
                const isSelected = category.id === filters.selectedId

                return (
                  <PillButton
                    key={category.id}
                    style="bordered"
                    size="small"
                    color={isSelected ? Color.Charcoal : Color.Gray}
                    overrideTextColor={Color.Charcoal}
                    text={category.label}
                    hideIcon
                    ariaPressed={isSelected}
                    onClick={() => filters.onSelect(category)}
                    className="!w-auto shrink-0 whitespace-nowrap"
                  />
                )
              })}
            </div>
          )}

          {showNavigationArrows && (
            <div className="mt-10 hidden justify-end gap-10 sm:flex md:col-start-3 md:mt-0 md:justify-self-end">
              <CircledIconButton
                color={Color.Charcoal}
                style="bordered"
                icon={<ArrowIcon className="rotate-180" />}
                ariaLabel="Previous products"
                disabled={isAtStart}
                onClick={() => swiperReference.current?.slidePrev()}
              />
              <CircledIconButton
                color={Color.Charcoal}
                style="bordered"
                icon={<ArrowIcon />}
                ariaLabel="Next products"
                disabled={isAtEnd}
                onClick={() => swiperReference.current?.slideNext()}
              />
            </div>
          )}
        </div>
      )}

      <div
        className={classNames(
          "-mx-gutter-xs hidden sm:-mx-gutter-sm sm:flex md:-mx-gutter-md lg:-mx-gutter-lg xl:-mx-gutter-xl 4k:-mx-gutter-4k",
          showNavigationArrows ? "mt-10 md:mt-20" : "mt-[38px]"
        )}
      >
        <Swiper
          className="w-full px-gutter-xs sm:px-gutter-sm md:px-gutter-md lg:px-gutter-lg xl:px-gutter-xl 4k:px-gutter-4k"
          slidesPerView="auto"
          watchOverflow
          centerInsufficientSlides
          onSwiper={
            isFiltering || showNavigationArrows
              ? (swiper) => {
                  swiperReference.current = swiper
                  updateEdgeState(swiper)
                }
              : undefined
          }
          onProgress={showNavigationArrows ? updateEdgeState : undefined}
          onResize={showNavigationArrows ? updateEdgeState : undefined}
        >
          {resultCards.map(({ item, card }, index) => (
            <SwiperSlide
              key={`${item.value}-${index}`}
              className={
                isFiltering
                  ? classNames("flex max-w-[388px] sm:w-[388px]", {
                      "!hidden": !visibleItemValues.has(item.value),
                      "mr-32": index !== lastVisibleIndex,
                    })
                  : "mr-32 flex max-w-[388px] last:mr-0 sm:w-[388px]"
              }
            >
              {card}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div className="mt-[38px] block sm:hidden">
        {resultCards.map(({ item, card }, index) => (
          <div
            key={`${item.value}-${index}`}
            className={
              isFiltering
                ? classNames("my-gutter-xs", {
                    hidden: !visibleItemValues.has(item.value),
                  })
                : "my-gutter-xs"
            }
          >
            {card}
          </div>
        ))}
      </div>
    </>
  )
}

export default SolutionDisplay
